<template>
    <div class="hts-gantt-journey">
        <hts-gantt-chart
            v-if="!loading"
            ref="chart"
            label="Danh sách xe"
            :rows="filteredRows"
            :items="items"
            @bar-click="handleBarClick"
            @selection-change="handleSelectionChange">
            <template #row-item="{ row }">
                <div class="flex justify-between w-full items-center">
                    <span class="ml-1">
                        {{ row.licensePlate }}
                    </span>
                    <div
                        v-if="row.parkingNodeName"
                        class="rounded border text-green-500 border-green-500 text-sm h-6 px-1">
                        <hts-tooltip>
                            <template #reference>
                                <span class="inline-block max-w-32 overflow-hidden text-ellipsis whitespace-nowrap">
                                    {{ row.parkingNodeName }}
                                </span>
                            </template>
                            <span>
                                Bãi xe: {{ row.parkingNodeName }}
                            </span>
                        </hts-tooltip>
                    </div>
                    <!--
                      <div
                      v-else
                      class="rounded border text-error-500 border-error-500 text-sm h-6 px-1 max-w-32 overflow-hidden text-ellipsis whitespace-nowrap">
                      chưa có bãi xe
                      </div>
                    -->
                </div>
            </template>
            <template #bar-item="{ item }">
                <hts-tooltip
                    :class="getJourneyClass(item)"
                    class="block w-full h-full border rounded whitespace-nowrap overflow-hidden text-ellipsis px-1">
                    <template #reference>
                        <span>
                            {{ item.code }}
                        </span>
                    </template>
                    <slot
                        name="bar-tooltip"
                        :item="item">
                        <p class="font-bold whitespace-nowrap">
                            <span class="text-blue-500">
                                {{ item?.code }}
                            </span>
                        </p>
                        <p class="font-bold whitespace-nowrap">
                            <span class="mr-1">
                                Đơn hàng:
                            </span>
                            <hts-link
                                :to="{ name: 'trucker-id-order', query: { action: 'view', orderId: item?.order } }"
                                target="_blank">
                                {{ item?.orderCode }}
                            </hts-link>
                        </p>
                        <hts-stage-names :items="item.stageNames" />
                        <div>
                            <span class="mt-2 mr-2 text-green-600 px-2 border rounded whitespace-nowrap inline-block align-middle">
                                {{ $formatters.datetime(item.timeIn) }}
                            </span>
                            <span class="mt-2 text-accent-600 px-2 border rounded whitespace-nowrap inline-block align-middle">
                                {{ $formatters.datetime(item.timeOut) }}
                            </span>
                        </div>
                    </slot>
                </hts-tooltip>
            </template>
        </hts-gantt-chart>
    </div>
</template>
<script lang="ts" setup>
const { $dayjs } = useNuxtApp()
const props = defineProps({
  loading: Boolean,
  showManualSelection: Boolean,
  search: {
    type: String,
    default: ''
  }
})
const emits = defineEmits(['item-click', 'selection-change'])
const { loading, showManualSelection, search } = toRefs(props)
const chart: any = ref(null)
const rows = ref<any[]>([])
const filteredRows = ref<any[]>([])
const items = ref<any[]>([])

watch(
  () => showManualSelection.value,
  (val: boolean) => {
    showSelection(val)
  }
)

watch(
  () => search.value,
  (val: string) => {
    setFilterRows(val)
  }
)

const filterRows = (s?: string) => {
  filteredRows.value = rows.value.filter((r: any) => {
    if (!s) {
      return true
    }
    if (!r.licensePlate) {
      return false
    }
    return r.licensePlate.toLowerCase().includes(s.toLowerCase())
  })
}
const setFilterRows = $lodash.debounce((s?: string) => {
  filterRows(s)
  nextTick(() => {
    chart.value && chart.value.refreshRows()
  })
}, 500)

const getJourneyClass = (journey: any) => {
  if (journey.vehicleId) {
    return ['bg-green-300']
  }
  if (journey.vehicleTempId) {
    return ['bg-orange-300']
  }
  if (journey.status === 'running') {
    return ['bg-blue-300']
  }
  return ['bg-gray-200']
}

/** UX methods */
const showSelection = (val: boolean) => {
  chart.value && chart.value.toggleSelection(val)
}

const handleSelectionChange = (id: any, val: boolean) => {
  emits('selection-change', id, val)
}

const clearSelection = () => {
  return chart.value && chart.value.clearSelection()
}

/** Set data methods */
const setRows = (data: any[]) => {
  if (!Array.isArray(data)) {
    return
  }
  items.value = []
  rows.value = data.map((r: any) => {
    Object.assign(r, {
      label: r.licensePlate
    })
    if (Array.isArray(r.journeys)) {
      r.journeys.forEach((j: any) => {
        if (!r.id) {
          return
        }
        if (!j.timeIn || !j.timeOut) {
          return
        }
        Object.assign(j, {
          rowId: r.id,
          label: j.code
        })
        items.value.push(j)
      })
    }
    return r
  })
  filterRows(search.value)
}

const getRows = () => {
  return rows.value
}
const getRowById = (id: string) => {
  return rows.value.find((r: any) => r.id === id)
}
const addRows = (data: any[]) => {}
const addRow = (data: { [key: string]: any }) => {
  if (!data) {
    return
  }
  const row = { ...data, ...{ label: data.code } }
  rows.value.push(row)
  return chart.value && chart.value.refreshRows()
}
const setRow = (data: { [key: string]: any }) => {
  if (!data) {
    return
  }
  const exists = rows.value.find((i: any) => i.id === data.id)
  if (!exists) {
    return
  }
  addRow(data)
  return chart.value && chart.value.refreshRows()
}
const deleteRow = () => {}

const setItems = (data: any[]) => {}
const addItem = (id: any, data: any) => {
  if (!id || !data) {
    return
  }
  Object.assign(data, { rowId: id, label: data.code })
  items.value.push(data)
  return chart.value && chart.value.refreshItems()
}
const addItems = (id: any, data: any[]) => {
  if (!Array.isArray(data)) {
    return
  }

  const tempItems = data
    .filter((item: any) => {
      return id || item.rowId
    })
    .map((item: any) => {
      Object.assign(item, { id: item.id || item._id, rowId: id || item.rowId, label: item.code })
      return item
    })
  items.value = [...items.value, ...tempItems]
  return chart.value && chart.value.refreshItems()
}
const setItem = (id: any, data: any) => {
  if (!id || !data) {
    return
  }
  const exists = items.value.findIndex((i: any) => i.id === data.id)
  if (exists < 0) {
    return addItem(id, data)
  }
  items.value[exists] = Object.assign(items.value[exists], { ...data, ...{ rowId: id, label: data.code } })
  return chart.value && chart.value.refreshItems()
}
const deleteItem = (data: any) => {
  if (!data) {
    return
  }
  const exists = items.value.findIndex((i: any) => i.id === data.id)
  if (exists >= 0) {
    items.value.splice(exists, 1)
  }
  return chart.value && chart.value.refreshItems()
}

const updateAndAddItem = (id: string, data: any) => {
  if (!data) {
    return
  }
  const exists = items.value.findIndex((i: any) => i.id === data.id && i.vehicleId !== id)
  if (exists >= 0) {
    items.value.splice(exists, 1)
    chart.value && chart.value.refreshItems()
  }
  return setItem(id, data)
}

const handleBarClick = (item: any, index: number, e: any) => {
  emits('item-click', item, index, e)
}

const onResize = () => {
  chart.value && chart.value.onResize()
}

defineExpose({
  showSelection,
  setItems,
  addItem,
  addItems,
  setItem,
  deleteItem,
  setRows,
  addRow,
  addRows,
  setRow,
  deleteRow,
  clearSelection,
  getRows,
  getRowById,
  updateAndAddItem,
  onResize
})
</script>
